/* eslint-disable complexity */
/* eslint-disable max-lines */
/* eslint-disable indent */
/* eslint-disable max-params */
/* eslint-disable max-statements */
import { useCallback, useMemo, useState } from 'react';
import type { useGetReferences } from './useGetReferences.js';
import type { useGetFlags } from './useGetFlags.js';

type LatestInteractionRegion =
  'calendar-days' | 'calendar-head' | 'timewheel' | 'date-inputs' | 'time-inputs' | 'clear-btn' | 'picker-icon' | '';
type CurrFocusDescriber =
  | 'first-segment'
  | 'month-input'
  | 'day-input'
  | 'year-input'
  | 'hour-input'
  | 'minute-input'
  | 'meridiem-input'
  | 'last-segment'
  | 'clear-btn'
  | 'date-picker-icon'
  | 'time-picker-icon'
  | 'datetime-picker-icon'
  | 'calendar-prev-month'
  | 'calendar-next-month'
  | 'calendar-prev-year'
  | 'calendar-next-year'
  | 'calendar-metafocused-day'
  | 'timewheel-curr-hour'
  | 'timewheel-curr-minute'
  | 'timewheel-curr-meridiem'
  | '';

interface Config {
  DOMRefs: ReturnType<typeof useGetReferences>;
  flags: ReturnType<typeof useGetFlags>;
}
export const useFocusTracker = ({ DOMRefs, flags }: Config) => {
  const [latestInteractionRegion, setLatestInteractionRegion] = useState<LatestInteractionRegion>('');
  const [currFocusDescriber, setCurrFocusDescriber] = useState<CurrFocusDescriber>('');
  const { firstSegment, lastSegment } = DOMRefs;
  const { isDateSelector, isTimeSelector, isControllerOnly } = flags;

  const trackFocusPicker = useCallback(() => {
    let picker = 'datetime-picker-icon' as CurrFocusDescriber;
    if (isDateSelector) picker = 'date-picker-icon';
    if (isTimeSelector) picker = 'time-picker-icon';
    if (latestInteractionRegion !== 'picker-icon') setLatestInteractionRegion('picker-icon');
    if (picker !== currFocusDescriber) setCurrFocusDescriber(picker);
  }, [currFocusDescriber, isDateSelector, isTimeSelector, latestInteractionRegion]);

  /*
   depending on the polymorphic props, the first segment can be:
    - month-input (when date inputs are present)
    - hour-input (when ONLY time inputs are present)
    - datetime-picker-icon (when picker is present and we are selecting both date and time)
    - date-picker-icon (when picker is present and no time selection is involved)
    - time-picker-icon (when picker is present and no date selection is involved)
  */
  const trackFocusFirstSegment = useCallback(() => {
    let region = 'date-inputs' as LatestInteractionRegion;
    if (firstSegment === 'hour-input') region = 'time-inputs';
    if (firstSegment === 'date-picker-icon') region = 'picker-icon';
    if (firstSegment === 'time-picker-icon') region = 'picker-icon';
    if (firstSegment === 'datetime-picker-icon') region = 'picker-icon';
    if (region !== latestInteractionRegion) setLatestInteractionRegion(region);
    if (firstSegment !== currFocusDescriber) setCurrFocusDescriber(firstSegment);
  }, [currFocusDescriber, firstSegment, latestInteractionRegion]);

  /*
   depending on the polymorphic props, the last segment can be:
    - year-input (when ONLY date inputs are present)
    - meridiem-input (when time inputs are present)
    - clear-btn (when clearable is true and there is no picker)
    - datetime-picker-icon (when picker is present and we are selecting both date and time)
    - date-picker-icon (when picker is present and no time selection is involved)
    - time-picker-icon (when picker is present and no date selection is involved)
  */
  const trackFocusLastSegment = useCallback(() => {
    let region = 'date-inputs' as LatestInteractionRegion;
    if (lastSegment === 'meridiem-input') region = 'time-inputs';
    if (lastSegment === 'clear-btn') region = 'clear-btn';
    if (lastSegment === 'date-picker-icon') region = 'picker-icon';
    if (lastSegment === 'time-picker-icon') region = 'picker-icon';
    if (lastSegment === 'datetime-picker-icon') region = 'picker-icon';
    if (region !== latestInteractionRegion) setLatestInteractionRegion(region);
    if (lastSegment !== currFocusDescriber) setCurrFocusDescriber(lastSegment);
  }, [currFocusDescriber, lastSegment, latestInteractionRegion]);

  const trackFocusResetter = useCallback(() => {
    if (latestInteractionRegion !== '') setLatestInteractionRegion('');
    if (currFocusDescriber !== '') setCurrFocusDescriber('');
  }, [currFocusDescriber, latestInteractionRegion]);

  const handleComponentBlurs = useCallback(
    (e: React.FocusEvent<HTMLElement>) => {
      /**
       * In controller only mode, we want to reset the focus tracker when the user moves focus outside of the component
       * this is because in controller only mode we don't have a portal that we need to account for,
       * so any blur event that happens when we are in controller only mode should be treated as a user moving focus outside of the component
       */
      if (isControllerOnly) {
        const relatedTarget = e.relatedTarget as HTMLElement | null;
        if (relatedTarget && (e.currentTarget as HTMLElement).contains(relatedTarget)) {
          return;
        }
        trackFocusResetter();
        return;
      }
      /* our code base is trying to avoid side-effects and we also try to be imperative
    due to how "portals" work, when we open a portal the "blur" event is triggered
    we need to distinguish between
     - a blur that is caused by a portal opening
           vs
     - a blur that is caused by a user moving focus to another element
    we can do this by checking the currFocusDescriber and latestInteractionRegion
    we know that blur is triggered by portal opening when:
      - latestInteractionRegion === 'calendar-days' && currFocusDescriber === 'calendar-metafocused-day'
        (this is the case when we open the calendar or calendarWithTimeWheel with a preselected date)
      - latestInteractionRegion === 'calendar-head' && currFocusDescriber === 'calendar-prev-month'
        (this is the case when we open the calendar or calendarWithTimeWheel without a preselected date)
      - latestInteractionRegion === 'timewheel' && currFocusDescriber === 'timewheel-curr-hour'
        (this is the case when we open any timewheel ONLY)
    */
      let shouldShortCircuit = false;
      // preselected date OR filled date value OR onCalendarOpenFocusedDay is present
      if (latestInteractionRegion === 'calendar-days' && currFocusDescriber === 'calendar-metafocused-day')
        shouldShortCircuit = true;
      // empty date value AND no onCalendarOpenFocusedDay is present
      if (latestInteractionRegion === 'calendar-head' && currFocusDescriber === 'calendar-prev-month')
        shouldShortCircuit = true;

      // only time wheel is present in the contextual region
      if (latestInteractionRegion === 'timewheel' && currFocusDescriber === 'timewheel-curr-hour')
        shouldShortCircuit = true;

      if (shouldShortCircuit) return;
      trackFocusResetter();
    },
    [currFocusDescriber, isControllerOnly, latestInteractionRegion, trackFocusResetter],
  );

  return useMemo(
    () => ({
      latestInteractionRegion,
      setLatestInteractionRegion,
      currFocusDescriber,
      setCurrFocusDescriber,
      trackFocusResetter,
      trackFocusMonthInput: () => {
        if (latestInteractionRegion !== 'date-inputs') setLatestInteractionRegion('date-inputs');
        if (currFocusDescriber !== 'month-input') setCurrFocusDescriber('month-input');
      },
      trackFocusDayInput: () => {
        if (latestInteractionRegion !== 'date-inputs') setLatestInteractionRegion('date-inputs');
        if (currFocusDescriber !== 'day-input') setCurrFocusDescriber('day-input');
      },
      trackFocusYearInput: () => {
        if (latestInteractionRegion !== 'date-inputs') setLatestInteractionRegion('date-inputs');
        if (currFocusDescriber !== 'year-input') setCurrFocusDescriber('year-input');
      },
      trackFocusHourInput: () => {
        if (latestInteractionRegion !== 'time-inputs') setLatestInteractionRegion('time-inputs');
        if (currFocusDescriber !== 'hour-input') setCurrFocusDescriber('hour-input');
      },
      trackFocusMinuteInput: () => {
        if (latestInteractionRegion !== 'time-inputs') setLatestInteractionRegion('time-inputs');
        if (currFocusDescriber !== 'minute-input') setCurrFocusDescriber('minute-input');
      },
      trackFocusMeridiemInput: () => {
        if (latestInteractionRegion !== 'time-inputs') setLatestInteractionRegion('time-inputs');
        if (currFocusDescriber !== 'meridiem-input') setCurrFocusDescriber('meridiem-input');
      },
      trackFocusClearBtn: () => {
        if (latestInteractionRegion !== 'clear-btn') setLatestInteractionRegion('clear-btn');
        if (currFocusDescriber !== 'clear-btn') setCurrFocusDescriber('clear-btn');
      },
      trackFocusDatePickerIcon: () => {
        if (latestInteractionRegion !== 'picker-icon') setLatestInteractionRegion('picker-icon');
        if (currFocusDescriber !== 'date-picker-icon') setCurrFocusDescriber('date-picker-icon');
      },
      trackFocusTimePickerIcon: () => {
        if (latestInteractionRegion !== 'picker-icon') setLatestInteractionRegion('picker-icon');
        if (currFocusDescriber !== 'time-picker-icon') setCurrFocusDescriber('time-picker-icon');
      },
      trackFocusDatetimePickerIcon: () => {
        if (latestInteractionRegion !== 'picker-icon') setLatestInteractionRegion('picker-icon');
        if (currFocusDescriber !== 'datetime-picker-icon') setCurrFocusDescriber('datetime-picker-icon');
      },
      trackFocusCalendarPrevMonth: () => {
        if (latestInteractionRegion !== 'calendar-head') setLatestInteractionRegion('calendar-head');
        if (currFocusDescriber !== 'calendar-prev-month') setCurrFocusDescriber('calendar-prev-month');
      },
      trackFocusCalendarNextMonth: () => {
        if (latestInteractionRegion !== 'calendar-head') setLatestInteractionRegion('calendar-head');
        if (currFocusDescriber !== 'calendar-next-month') setCurrFocusDescriber('calendar-next-month');
      },
      trackFocusCalendarPrevYear: () => {
        if (latestInteractionRegion !== 'calendar-head') setLatestInteractionRegion('calendar-head');
        if (currFocusDescriber !== 'calendar-prev-year') setCurrFocusDescriber('calendar-prev-year');
      },
      trackFocusCalendarNextYear: () => {
        if (latestInteractionRegion !== 'calendar-head') setLatestInteractionRegion('calendar-head');
        if (currFocusDescriber !== 'calendar-next-year') setCurrFocusDescriber('calendar-next-year');
      },
      trackFocusCalendarMetafocusedDay: () => {
        if (latestInteractionRegion !== 'calendar-days') setLatestInteractionRegion('calendar-days');
        if (currFocusDescriber !== 'calendar-metafocused-day') setCurrFocusDescriber('calendar-metafocused-day');
      },
      trackFocusTimewheelCurrHour: () => {
        if (latestInteractionRegion !== 'timewheel') setLatestInteractionRegion('timewheel');
        if (currFocusDescriber !== 'timewheel-curr-hour') setCurrFocusDescriber('timewheel-curr-hour');
      },
      trackFocusTimewheelCurrMinute: () => {
        if (latestInteractionRegion !== 'timewheel') setLatestInteractionRegion('timewheel');
        if (currFocusDescriber !== 'timewheel-curr-minute') setCurrFocusDescriber('timewheel-curr-minute');
      },
      trackFocusTimewheelCurrMeridiem: () => {
        if (latestInteractionRegion !== 'timewheel') setLatestInteractionRegion('timewheel');
        if (currFocusDescriber !== 'timewheel-curr-meridiem') setCurrFocusDescriber('timewheel-curr-meridiem');
      },
      trackFocusPicker,
      trackFocusFirstSegment,
      trackFocusLastSegment,
      handleComponentBlurs,
    }),
    [
      latestInteractionRegion,
      currFocusDescriber,
      trackFocusResetter,
      trackFocusPicker,
      trackFocusFirstSegment,
      trackFocusLastSegment,
      handleComponentBlurs,
    ],
  );
};
